const core = require('./core');

const pricing = {
  /**
   * Lookup the matching mileage band for the provided distance in miles
   * and class of the move
   *
   *        $miles > start
   *    and $miles <= end
   *    and (class = $class OR class = 'blended')
   *
   * @param  {int} miles - The number of miles distance (ex: 6.6 mi)
   * @param  {string} classType - The class of the band to get
   * @param  {MileageBand[]} bandTable - An array of mileage bands
   *            [
   *              0: {distance_start: 0, distance_end: 5, rate: 45, type: 'flat', class: 'stranded'},
   *              1: {distance_start: 5, distance_end: 10, rate: 8, type: 'per', class: 'stranded'},
   *              2: {distance_start: 10, distance_end: 20, rate: 6, type: 'per', class: 'stranded'},
   *              3: {distance_start: 20, distance_end: 30, rate: 4.5, type: 'per', class: 'stranded'},
   *              4: {distance_start: 30, distance_end: 40, rate: 3.5, type: 'per', class: 'stranded'}
   *            ]
   * @return {MileageBand} - The mileage band that matches based on the input miles.
   *          The return value will be null if no matching mileage band is found
   */
  getMileageBand: function (miles, classType, bandTable) {
    if (!Array.isArray(bandTable)) {
      return null;
    }

    try {
      let matches = bandTable.filter(band => {
        return (
          band.distance_start < miles &&
          band.distance_end >= miles &&
          (band.class === classType || band.class === 'blended')
        );
      });
      if (matches.length > 0) {
        return matches[0];
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  },

  /**
   * Get the price charged to the customer for the move
   *
   * @param  {int} miles - The number of miles distance (ex: 6.6 mi)
   * @param  {int} flatRate - The dollars to apply as a flat rate
   * @param  {int} perRate - The dollars to apply as a per mile rate
   * @param  {string} rateType - The type of rate which can only be flat or per
   * @param  {int} discount - The dollars to apply as a discount to the price
   * @param  {int} tolls - The dollars to add to the price for expected tolls on route
   * @param  {int} milageBandStart - Number of miles to be excluded from per mile rate
   * @return  {int} - The price to charge the customer in dollars
   */
  getCustomerPrice: function (miles = 0, flatRate = 0, perRate = 0, rateType, discount = 0, tolls = 0, milageBandStart = 0) {
    if (miles <= 0) return 0;
    if (flatRate <= 0 && perRate <= 0) return 0;
    if (!(rateType === 'per' || rateType === 'flat' || rateType === 'flatPlusPer')) return 0;
    if (tolls < 0) return 0;

    let price = 0;

    if (rateType === 'flat') {
      price = flatRate;
    } else if (rateType === 'per') {
      price = perRate * miles;
    } else if (rateType === 'flatPlusPer') {
      // We do not want to charge the per mile rate for the first "X" miles, represented by milageBandStart
      // example: 40 to 100 milage band with a $100 flat rate and $1 per mile; 60 mile move.
      //  the customer will be charged $100 for the first 40 miles and $1 per mile for the remaining 20 miles = $120
      price = flatRate + (perRate * (miles - milageBandStart));
    }

    // Add tolls and subtract discount from final price
    price = price + tolls - discount;
    
    // Ensure price doesn't go below 0
    price = Math.max(0, price);
    
    // Round to 2 decimal places
    return Math.round(price * 100) / 100;
  },
};

module.exports = pricing;